import styled from '@emotion/styled'
import * as mixins from 'view/styles/mixins'
import * as constants from 'view/styles/constants'
import { maxSeverity, Severity } from 'model'
import { BaseInputSpan, BaseInputProps, BorderModifier } from '../inputs'
import { IconButton, LinkButton, TextButton, MultiButton } from '../buttons'
import { Listbox, OptionTemplateProps, OptionKey } from './Listbox'

export const DropdownListbox = styled(Listbox)`
  background: ${constants.CORE_PRIMARY};
  padding: 6px;
`

export const DropdownTitle = styled.div`
  font-weight: ${constants.FontWeight.SemiBold};
  padding: 4px 0 6px 4px;
`

export const SelectboxPrimary = styled(BaseInputSpan)<BaseInputProps>`
  padding: 0;
  display: flex;
  align-items: center;
  cursor: ${props => (props.disabled ? 'default' : 'pointer')};
  background-color: ${props => getBackgroundColour(props, BorderModifier.Normal)};
  &:focus-within {
    background-color: ${props => getBackgroundColour(props, BorderModifier.Focused)};
  }
  &:hover:not(:focus-within) {
    background-color: ${props => getBackgroundColour(props, BorderModifier.Hover)};
  }
`

export const SelectboxInput = styled.input`
  ${mixins.noCollapse}
  background-color: ${constants.CORE_PRIMARY};
  width: 100%;
  height: 24px;
  border-radius: 6px;
  padding-left: 6px;  
  border: none;
  outline: 0;
  cursor: inherit;
`

export const DropdownButton = styled(IconButton)<BaseInputProps>`
  flex: 0 0 24px;
  padding: 0 6px 2px 6px;
  color: ${props => getDropdownButtonColour(props, BorderModifier.Normal)};
  ${SelectboxPrimary}:focus-within & {
    color: ${props => getDropdownButtonColour(props, BorderModifier.Focused)};
  }
  ${SelectboxPrimary}:hover:not(:focus-within) & {
    color: ${props => getDropdownButtonColour(props, BorderModifier.Hover)};
  }
`

export function getBackgroundColour(props: BaseInputProps, modifier: BorderModifier) {
  const severity = maxSeverity(props.validationIssues)

  if (severity === Severity.ERROR) {
    return constants.ACCENT_BAD
  } else if (severity === Severity.WARN) {
    return constants.ACCENT_BE_AWARE
  } else if (props.disabled || props.readOnly) {
    return constants.CORE_PRIMARY
  } else if (modifier === BorderModifier.Focused) {
    return constants.ACCENT_PRIMARY
  } else if (modifier === BorderModifier.Hover) {
    return constants.CORE_PRIMARY_5
  } else {
    return constants.CORE_PRIMARY
  }
}

export function getDropdownButtonColour(props: BaseInputProps, modifier: BorderModifier) {
  const severity = maxSeverity(props.validationIssues)

  if (severity) {
    return constants.CORE_PRIMARY
  } else if (props.disabled || props.readOnly) {
    return constants.CORE_PRIMARY_4
  } else if (modifier === BorderModifier.Normal) {
    return constants.CORE_PRIMARY_7
  } else {
    return constants.CORE_PRIMARY
  }
}

export const SelectboxOptionTemplate = styled.div<OptionTemplateProps<any, OptionKey>>`
  width: 100%;
  display: flex;
  align-items: center;
  padding: 0 6px;
  border-radius: 3px;
  color: ${props => (props.selected ? constants.CORE_PRIMARY : constants.CORE_PRIMARY_7)};
  background-color: ${props =>
    props.selected ? constants.ACCENT_PRIMARY : props.highlighted ? constants.CORE_PRIMARY_1 : constants.CORE_PRIMARY};
  &:hover {
    color: ${props => (props.selected ? constants.CORE_PRIMARY : constants.CORE_PRIMARY_7)};
    background-color: ${props => (props.selected ? constants.ACCENT_PRIMARY_1 : constants.CORE_PRIMARY_1)};
    text-decoration: underline;
  }
`

// MultiCombobox / GroupedMultiCombobox

export const MultiComboboxWrapper = styled.div<{ horizontalLayout?: boolean }>`
  ${props =>
    props.horizontalLayout &&
    `
      display: flex;
      align-items: center;
  `}
`

export const ClearButton = styled(LinkButton)`
  width: 100%;
  margin-top: 8px;
  text-align: right;
`

export const SelectedListbox = styled(Listbox)<{ marginTop?: number; horizontalLayout?: boolean }>`
  margin-top: ${props => props.marginTop ?? '4'}px;
  border: 1px solid ${constants.CORE_PRIMARY_2};
  border-radius: 6px;
  li {
    height: 34px;
  }
  ${props =>
    props.horizontalLayout &&
    `
    flex-grow: 1;
    margin-left: 5px;
    margin-top: 0px;
    li {
      height: 24px;
    }
  `}
`

export const SelectButton = styled(TextButton)<{ selected: boolean }>`
  min-width: 50px;
  flex: 0 0 50px;
  height: 24px;
  display: ${props => (props.selected ? 'flex' : 'none')};
  justify-content: center;
  align-items: center;
`

export const DropdownOption = styled(SelectboxOptionTemplate)`
  justify-content: space-between;
  padding-right: 1px;

  &:hover {
    text-decoration: none;
  }
  &:hover span {
    text-decoration: underline;
  }
  &:hover button {
    display: flex;
  }
`

export const SelectedOption = styled.div<OptionTemplateProps<any, OptionKey>>`
  width: 100%;
  display: flex;
  align-items: center;
  border-bottom: 1px solid #e1e3e7;
  justify-content: space-between;
  padding: 0 6px;
  border-left: 6px solid ${props => props.optionColor || 'gray'};

  li:only-child > & {
    border-top: none;
    border-bottom: none;
  }
  li:first-of-type > & {
    border-top: none;
    border-top-left-radius: 6px;
  }
  li:last-child > & {
    border-bottom: none;
    border-bottom-left-radius: 6px;
  }
`

export const RemoveItemButton = styled(MultiButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  svg {
    width: 8px;
    height: 8px;
  }
  &:hover {
    svg {
      color: red;
    }
  }
`
